import {getLogDomain, getLogStatus} from '@/modules/system-logs/functions'
import {getLogMessage, getLogName} from '@/modules/system-logs/log-message'
import {DisplayColDef} from '@cypro/ui/types'
import {Avatar, Badge, Box, chakra, Icon, Text} from '@chakra-ui/react'
import {createColumnHelper} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {IconType} from 'react-icons/lib'
import {
  LuBriefcaseMedical,
  LuCircleAlert,
  LuCircleCheck,
  LuCircleX,
  LuContactRound,
  LuInfo,
  LuSettings,
  LuShield,
  LuTriangleAlert,
} from 'react-icons/lu'
import {EventLog} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components'
import {getMonthDayTimeFormatter} from '@cypro/ui/utils'

export const columnHelper = createColumnHelper<EventLog & {isHeader?: boolean}>()

type DisplayColumnDef = DisplayColDef<EventLog & {isHeader?: boolean}>

export function getEventIcon(domain?: string | null): IconType {
  const d = (domain || '').toLowerCase()

  if (/auth/.test(d)) return LuShield
  if (/patient/.test(d)) return LuContactRound
  if (/hl7/.test(d)) return LuBriefcaseMedical
  if (/configuration/.test(d)) return LuSettings

  return LuInfo
}

export function getStatusIcon(status: 'OK' | 'FAILED' | 'WARNING' | 'INFO' | null) {
  switch (status) {
    case 'OK':
      return <Icon as={LuCircleCheck} color="green.500" boxSize={5} />
    case 'FAILED':
      return <Icon as={LuCircleX} color="red.500" boxSize={5} />
    case 'INFO':
      return <Icon as={LuCircleAlert} color="blue.500" boxSize={5} />
    case 'WARNING':
      return <Icon as={LuTriangleAlert} color="yellow.500" boxSize={5} />
    default:
      return <Icon as={LuCircleX} color="red.500" boxSize={5} />
  }
}

const formatDayKey = (value: number) => {
  return getMonthDayTimeFormatter().format(value)
}

export const SystemLogsColumnsDef = {
  dayKeyColumn: (def?: DisplayColumnDef) =>
    columnHelper.display({
      id: 'dayKey',
      size: 15,
      minSize: 15,
      cell: ({row}) => {
        const formatted = formatDayKey(row.original.performedAt)
        return (
          <chakra.span fontSize="xs" color="fg.subtle">
            {formatted}
          </chakra.span>
        )
      },
      ...def,
    }),

  userAction: (def?: DisplayColumnDef) =>
    columnHelper.display({
      id: 'userKey',
      cell: ({row}) => {
        const {t} = useTranslate('common')
        const {t: tE} = useTranslate('events')
        const actor = row.original.actor?.displayUsername || row.original.actor?.name || 'System'

        return (
          <Box padding={3} display="flex" flexDirection="column" gap="4px">
            <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
              <Avatar.Root size="xs" data-e2e="current-user">
                <Avatar.Fallback name={actor} />
              </Avatar.Root>

              <Text fontSize="sm">{actor}</Text>

              <Text fontSize="sm" color="fg.subtle">
                {getLogName(row.original, t, tE)}
              </Text>
            </Box>
          </Box>
        )
      },
      ...def,
    }),

  /** `hidePatient` is for lists already scoped to one patient: no patient badge, and the `event.patient_message` text when there is one. */
  domainColumn: (def?: DisplayColumnDef, {hidePatient = false}: {hidePatient?: boolean} = {}) =>
    columnHelper.display({
      id: 'domainColumn',
      cell: ({row}) => {
        const {t} = useTranslate('common')
        const {t: tE} = useTranslate('events')
        const domain = getLogDomain(row.original)
        const eventMessage = getLogMessage(row.original, t, tE, {hidePatient})
        const patientName = row.original.details?.name ?? row.original.details?.patientName

        return (
          <Box padding={3} display="flex" flexDirection="row" alignItems="center" gap="8px">
            <Badge display="inline-flex" alignItems="center" gap={1} variant="subtle" px={2} py={0.5} rounded="md">
              <Icon as={getEventIcon(domain)} boxSize={3.5} />
              <Text fontSize="xs">{t(`event.domain.${domain}`)}</Text>
            </Badge>

            <Tooltip content={eventMessage}>
              <Text fontSize="sm" color="fg.subtle">
                {eventMessage}
              </Text>
            </Tooltip>

            {!hidePatient && Boolean(patientName) && (
              <Badge variant="outline" backgroundColor="white" px={2} py={0.5} rounded="md">
                {patientName}
              </Badge>
            )}
          </Box>
        )
      },
      ...def,
    }),

  statusColumn: (def?: DisplayColumnDef) =>
    columnHelper.display({
      id: 'statusColumn',
      size: 10, // small
      minSize: 10,
      cell: ({row}) => (
        <Box padding={3} display="flex" flexDirection="row" alignItems="center" gap="8px">
          {getStatusIcon(getLogStatus(row.original))}
        </Box>
      ),
      ...def,
    }),
}
